import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import {
  Comment,
  type CommentParentEntity,
  createUserDisplayName,
  FirestoreComment,
  FirestoreEntity,
  FirestoreUser,
} from '@jessie/commons';
import { LastEditorService } from './last-editor.service';
import { filter, firstValueFrom, Observable } from 'rxjs';
import { FIRESTORE_SERVICE, ORGA_FIRESTORE_SERVICE } from './firestore.service';
import { orderBy, where } from '@angular/fire/firestore';
import { SessionStateService } from './session-state.service';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  private injector = inject(EnvironmentInjector);
  private orgaFirestoreService = inject(ORGA_FIRESTORE_SERVICE);
  private rootFirestoreService = inject(FIRESTORE_SERVICE);
  private lastEditor = inject(LastEditorService);
  private sessionState = inject(SessionStateService);

  getAllComments<T>(parent: FirestoreEntity<T>) {
    return this.rootFirestoreService.getDocs<Comment>(
      parent.docRef.path + '/comment',
      runInInjectionContext(this.injector, () => orderBy('creationDate', 'desc'))
    );
  }

  getNotReactedComments(): Observable<FirestoreComment[] | null> {
    return this.sessionState.getUser().pipe(
      filter((user): user is FirestoreUser => !!user),
      switchMap((user) =>
        this.sessionState
          .getOrgaId()
          .pipe(
            switchMap((orgaId) =>
              this.orgaFirestoreService.getDocsByCollectionGroup<Comment>(
                'comment',
                runInInjectionContext(this.injector, () => where('orgaId', '==', orgaId)),
                runInInjectionContext(this.injector, () => where('notReactedUserIds', 'array-contains', user.id)),
                runInInjectionContext(this.injector, () => orderBy('creationDate', 'asc'))
              )
            )
          )
      )
    );
  }

  async getDocumentUserMentionCount() {
    const user = await firstValueFrom(
      this.sessionState.getUser().pipe(filter((user): user is FirestoreUser => !!user))
    );
    const orgaId = await firstValueFrom(this.sessionState.getOrgaId());

    return runInInjectionContext(this.injector, () =>
      this.orgaFirestoreService.getDocsCountByCollectionGroup(
        'comment',
        where('orgaId', '==', orgaId),
        where('notReactedUserIds', 'array-contains', user.id)
      )
    );
  }

  async createComment<T>(
    parent: FirestoreEntity<T>,
    parentEntity: CommentParentEntity,
    comment: Partial<Comment>
  ) {
    const user = await firstValueFrom(
      this.sessionState.getUser().pipe(filter((user): user is FirestoreUser => !!user))
    );
    const orgaId = await firstValueFrom(this.sessionState.getOrgaId());
    if (orgaId) {
      comment = {
        ...comment,
        orgaId,
        creationDate: new Date().toISOString(),
        authorDisplayName: createUserDisplayName(user.data.firstName, user.data.lastName),
        authorUserId: user.id,
        parentEntity: parentEntity,
        parentId: parent.id,
      };
      return this.rootFirestoreService.createDoc(
        parent.docRef.path + '/comment',
        await this.lastEditor.hydrate(comment)
      );
    } else {
      throw new Error('no orgaId set');
    }
  }

  async deleteComment(comment: FirestoreComment) {
    // first an update to have a last editor set...
    await this.updateComment(comment.docRef.path, {});
    return this.rootFirestoreService.deleteDoc(comment.docRef.path);
  }

  async updateComment(path: string, comment: Partial<Comment>): Promise<void> {
    return this.rootFirestoreService.updateDoc<Comment>(
      path,
      await this.lastEditor.hydrate(comment)
    );
  }
}
